/**
 * @file YouTubePlaylistCarousel component
 *
 * Fetches videos from a YouTube playlist and displays them in a MediaCarousel
 */

import SelectorEngine from '../../utils/dom/selector-engine';
import BaseComponent from '../BaseComponent';
import Request from '../../utils/request';
import MediaCarousel from '../MediaCarousel';
import Manipulator from '../../utils/dom/manipulator';
import '../VideoEmbed';

const NAME = 'YouTubePlaylistCarousel';

class YouTubePlaylistCarousel extends BaseComponent {
	constructor(element) {
		super(element);

		const { videosEmbedded, blockId, cacheDuration, blockEndpoint } = Manipulator.getDataAttributes(element);
		this.blockId = blockId;
		this.cacheDuration = cacheDuration;
		this.blockEndpoint = blockEndpoint && String(blockEndpoint).trim() ? blockEndpoint.trim() : '/api/youtube/block';
		this.videosEmbedded = videosEmbedded === true || videosEmbedded === 'true';

		// Pre-rendered if data attribute set OR content already has slides (server-rendered with videos prop)
		const contentEl = SelectorEngine.findOne('.lta-youtube-playlist-carousel__content', element);
		const hasSlides = contentEl && SelectorEngine.findOne('.swiper-slide', contentEl);
		const isPreRendered = this.videosEmbedded || hasSlides;

		if (isPreRendered) {
			this.initPreRendered();
		} else if (this.blockId) {
			this.init();
		}
	}

	static get NAME() {
		return NAME;
	}

	/**
	 * When videos are pre-rendered (passed from server), just init MediaCarousel. No API call.
	 */
	initPreRendered() {
		const contentElement = SelectorEngine.findOne('.lta-youtube-playlist-carousel__content', this._element);
		const carouselWrapper = contentElement && SelectorEngine.findOne('.lta-media-carousel', contentElement);
		if (carouselWrapper && SelectorEngine.findOne('.swiper-slide', carouselWrapper)) {
			requestAnimationFrame(() => {
				let carouselInstance = MediaCarousel.getInstance(carouselWrapper);
				if (carouselInstance?.swiperInstance) {
					carouselInstance.swiperInstance.destroy(true, true);
					carouselInstance.dispose();
				}
				carouselInstance = new MediaCarousel(carouselWrapper);
			});
		}
	}

	async init() {
		try {
			await this.fetchAndRenderVideos();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error loading YouTube playlist:', error, {
				blockId: this.blockId,
				element: this._element
			});
			this.showError(error.message || 'Failed to load videos');
		}
	}

	async fetchAndRenderVideos() {
		const loadingElement = SelectorEngine.findOne('.lta-youtube-playlist-carousel__loading', this._element);
		const contentElement = SelectorEngine.findOne('.lta-youtube-playlist-carousel__content', this._element);
		const errorElement = SelectorEngine.findOne('.lta-youtube-playlist-carousel__error', this._element);

		if (!this.blockId) {
			this.showError('Block ID is required');
			return;
		}

		try {
			const query = { blockId: this.blockId };
			if (this.cacheDuration != null && this.cacheDuration !== '') {
				query.cacheDuration = this.cacheDuration;
			}
			const response = await Request.get(this.blockEndpoint, query);
			if (response?.errors?.errorCode !== '00' || !response?.response) {
				if (response?.error?.message?.includes('quota') || response?.error?.message?.includes('exceeded')) {
					throw new Error('YouTube API quota exceeded. Please try again later or contact the administrator.');
				}
				throw new Error(response?.error?.message || 'Failed to fetch block');
			}
			if (response.response?.type !== 'playlist' || !Array.isArray(response.response?.videos)) {
				throw new Error('Invalid block response');
			}
			const videos = response.response.videos;
			if (!Array.isArray(videos) || videos.length === 0) {
				this.showError('No videos found in this playlist.');
				return;
			}

			if (loadingElement) loadingElement.style.display = 'none';
			if (errorElement) errorElement.style.display = 'none';
			
			if (contentElement) {
				this.renderCarousel(contentElement, videos);
				contentElement.style.display = 'block';

				// Initialize MediaCarousel after content is visible and slides are rendered
				// Use requestAnimationFrame to ensure DOM is updated
				requestAnimationFrame(() => {
					// Find the MediaCarousel wrapper element within this specific content element
					const carouselWrapper = SelectorEngine.findOne('.lta-media-carousel', contentElement);
					if (carouselWrapper) {
						// Check if MediaCarousel is already initialized for this element
						let carouselInstance = MediaCarousel.getInstance(carouselWrapper);
						
						// If already initialized, destroy it first to avoid conflicts
						if (carouselInstance?.swiperInstance) {
							carouselInstance.swiperInstance.destroy(true, true);
							carouselInstance.dispose();
						}
						
						// Create a new instance with the updated slides
						carouselInstance = new MediaCarousel(carouselWrapper);
					} else {
						// eslint-disable-next-line no-console
						console.error('MediaCarousel wrapper not found in content element', contentElement);
					}
				});
			} else {
				// eslint-disable-next-line no-console
				console.error('Content element not found for YouTube playlist carousel', this._element);
				this.showError('Content container not found');
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Error fetching YouTube videos:', error, {
				blockId: this.blockId,
				element: this._element
			});
			if (loadingElement) loadingElement.style.display = 'none';
			this.showError(error.message || 'Failed to load videos');
		}
	}

	renderCarousel(container, videos) {
		// Ensure we're scoping to this specific container to avoid injecting into other carousels
		if (!container || !container.contains) {
			// eslint-disable-next-line no-console
			console.error('Invalid container for renderCarousel', container);
			return;
		}

		const carouselElement = SelectorEngine.findOne('.lta-media-carousel', container);
		if (!carouselElement) {
			// eslint-disable-next-line no-console
			console.error('MediaCarousel element not found in container', container);
			return;	
		}

		const swiperElement = SelectorEngine.findOne('.swiper', carouselElement);
		const swiperWrapper = SelectorEngine.findOne('.swiper-wrapper', swiperElement);

		// Clear existing content in this specific wrapper
		swiperWrapper.innerHTML = '';

		videos.forEach((video) => {
			const slide = this.createSlide(video);
			swiperWrapper.appendChild(slide);
		});
	}

	createSlide(video) {
		const slide = document.createElement('div');
		slide.className = 'swiper-slide';
		slide.setAttribute('data-is-video', 'true');

		const publishedDate = video.publishedAt ? new Date(video.publishedAt).toLocaleDateString('en-GB', {
			weekday: 'long',
			day: 'numeric',
			month: 'long'
		}) : '';

		const videoEmbed = document.createElement('figure');
		videoEmbed.className = 'lta-video-embed';
		videoEmbed.id = 'youtube';
		videoEmbed.setAttribute('data-video-name', video.title || '');
		videoEmbed.setAttribute('data-is-private', 'false');
		videoEmbed.setAttribute('data-video-url', video.videoUrl || '');
		videoEmbed.setAttribute('data-video-date', video.publishedAt || '');

		const script = document.createElement('script');
		script.type = 'application/ld+json';
		const name = video.title || '';
		const description = video.description || '';
		const thumbnail = video.thumbnail || '';
		const publishedAt = video.publishedAt || '';
		const duration = video.duration || '';
		const videoUrl = video.videoUrl || '';
		const embedUrl = video.embedUrl || '';	
		const schema = {
			"@context":"https://schema.org",
			"@type":"VideoObject",
			"name": name,
			"description": description,
			"thumbnailUrl": thumbnail,
			"uploadDate": publishedAt,
			"duration": duration,
			"contentUrl": videoUrl,
			"embedUrl": embedUrl,
			"author":{"@type":"Organization","name":"LTA - Lawn Tennis Association"},
			"publisher":{"@type":"Organization","name":"LTA - Lawn Tennis Association","logo":{"@type":"ImageObject","url":"https://www.lta.org.uk/490e9f/siteassets/global/lta-general-use-logo.svg"}}
		};
		script.innerHTML = JSON.stringify(schema);

		const liteVideo = document.createElement('lite-video');
		liteVideo.className = 'lta-video-embed__player';
		liteVideo.setAttribute('videotype', 'youtube');
		liteVideo.setAttribute('videotitle', video.title || 'Video');
		
		if (video.thumbnail?.trim()) {
			liteVideo.setAttribute('fallback-thumbnail', video.thumbnail);
		}
		
		videoEmbed.appendChild(liteVideo);
		
		if (video.videoId) {
			requestAnimationFrame(() => {
				if (liteVideo.isConnected && liteVideo.shadowRoot && liteVideo.domRefImg) {
					liteVideo.setAttribute('videoid', video.videoId);
				}
			});
		}

		if (video.title) {
			const captionBackdrop = document.createElement('div');
			captionBackdrop.className = 'lta-video-embed__caption-backdrop u-rounded-b';

			const figcaption = document.createElement('figcaption');
			figcaption.className = 'lta-video-embed__caption u-rounded-b';
			const captionSpan = document.createElement('span');
			captionSpan.textContent = video.title;
			if (publishedDate) {
				captionSpan.textContent += ` | ${publishedDate}`;
			}
			figcaption.appendChild(captionSpan);

			videoEmbed.appendChild(captionBackdrop);
			videoEmbed.appendChild(figcaption);
		}

		slide.appendChild(videoEmbed);
		slide.appendChild(script);
		return slide;
	}

	showError(message) {
		const loadingElement = SelectorEngine.findOne('.lta-youtube-playlist-carousel__loading', this._element);
		const contentElement = SelectorEngine.findOne('.lta-youtube-playlist-carousel__content', this._element);
		const errorElement = SelectorEngine.findOne('.lta-youtube-playlist-carousel__error', this._element);

		if (loadingElement) loadingElement.style.display = 'none';
		if (contentElement) contentElement.style.display = 'none';
		if (errorElement) {
			const errorText = SelectorEngine.findOne('p', errorElement);
			if (errorText) {
				errorText.textContent = message || 'Failed to load videos. Please try again later.';
			}
			errorElement.style.display = 'block';
		} else {
			// eslint-disable-next-line no-console
			console.error('Error element not found for YouTube playlist carousel', this._element, message);
		}
	}
}

export default YouTubePlaylistCarousel;
